import { forwardRef } from 'react';
import classes from './HuginnLoader.module.css';

/**
 * The alternating mark — the only loading indicator in the product.
 *
 * Two beaks take turns: the left leans in and brightens, then the right. Call and response, two
 * ravens. It replaces a version where both beaks scaled and closed on each other at once, which
 * read as pounding and — because that mark was `HuginnMark`'s 1.75:1 geometry centred in a square
 * box — looked stretched at every size. Nothing scales here, so nothing can.
 *
 * There are two ways in, and they are deliberate:
 *
 *   1. **You never import this for a spinner.** `theme.js` registers it as Mantine's loader, so
 *      every `<Loader />`, every `<Button loading>` and every `<ActionIcon loading>` already
 *      renders it. A CTA the user clicks while staying on the page beats in its own icon slot —
 *      no separate spinner, no layout shift.
 *   2. **`<PageLoader />` when a whole page or panel has nothing to show yet.** Centred, with a
 *      halo, at a size that reads as "the screen is loading" rather than "a control is busy".
 *
 * One motion, two dresses, and the split is by size rather than by taste. At 14–16px in a button
 * two thin strokes read as noise, so the circled form gives the glyph a body — and it is the
 * favicon, which is the form the user has already seen in the tab. At 40–52px the alternation
 * carries on its own and a ring would just be a second thing to look at, so the page form is
 * bare — and that is the header lockup. Each context borrows the form it already knows.
 *
 * Geometry is the favicon's proportions (≈24 × 19.7 on the 40 grid), not `HuginnMark`'s. The
 * header lockup keeps its own, wider, because it sits beside text and has the room.
 */

// Bare: the favicon's proportions. Gap between the tips is 8.8 against a 3.9 stroke — the same
// 2.2 ratio the favicon settled on, below which two round-capped strokes fuse into an X.
const BARE_LEFT = 'M8 10.2 L15.6 20 L8 29.8';
const BARE_RIGHT = 'M32 10.2 L24.4 20 L32 29.8';

// Circled: pulled inward so a beak at full travel never crosses the ring.
const RING_LEFT = 'M10 12.8 L16.2 20 L10 27.2';
const RING_RIGHT = 'M30 12.8 L23.8 20 L30 27.2';

const HuginnLoader = forwardRef(function HuginnLoader(props, ref) {
  // `size` and `variant` are Mantine's — it hands them to whatever component backs the loader.
  // They must not reach the DOM. `bare` is ours, and is deliberately not spelled `variant`:
  // reusing Mantine's prop for our own switch would couple the dress to whatever a caller
  // happened to pass through `<Loader variant=…>`.
  const { className, size, variant, bare = false, ...others } = props;

  return (
    <span
      ref={ref}
      className={className ? `${classes.root} ${className}` : classes.root}
      // Which dress is on. Not decoration: it is the only thing distinguishing the two forms
      // from outside the component, and it is how the tests reach the branch without walking
      // the DOM. `data-testid` gives the same tests a handle on the root — Mantine renders this
      // component, so there is no other way to address it.
      data-testid="huginn-loader"
      data-dress={bare ? 'bare' : 'circled'}
      {...others}
    >
      <svg className={classes.mark} viewBox="0 0 40 40" aria-hidden="true" focusable="false">
        {!bare && <circle className={classes.ring} cx="20" cy="20" r="18" />}
        <path
          className={`${classes.beak} ${classes.left}`}
          d={bare ? BARE_LEFT : RING_LEFT}
        />
        <path
          className={`${classes.beak} ${classes.right}`}
          d={bare ? BARE_RIGHT : RING_RIGHT}
        />
      </svg>
    </span>
  );
});

export default HuginnLoader;

/**
 * A page or panel with nothing to render yet.
 *
 * `label` is off by default. A screen that is plainly mid-load does not need to be told so; a
 * label earns its place only when the wait is long enough that the user starts wondering what is
 * happening ("Reading evidence…"), and then it should name the work, not the state.
 */
export function PageLoader({
  size = 40,
  label,
  py = 48,
  // Fills the area a page gets, so the mark lands in the middle of what the reader is looking at
  // rather than tucked under the header. It was `padding: 48px` in normal flow, which put it near
  // the top of an otherwise empty screen — technically centred, and nowhere near the eye.
  //
  // Overridable because this is not always a page: the evidence drawer and FullPageLoader both
  // pass 0, the first because a viewport-tall loader inside a drawer invents a scrollbar, the
  // second because it is already centring in 100vh itself.
  minHeight = 'var(--rm-shell-content, 60vh)',
  ...rest
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        paddingTop: py,
        paddingBottom: py,
        width: '100%',
        minHeight,
      }}
      {...rest}
    >
      <span className={classes.stage} style={{ width: size, height: size }}>
        <span className={classes.halo} />
        <HuginnLoader bare style={{ '--loader-size': `${size}px`, position: 'relative' }} />
      </span>
      {/* Visible when there is something worth saying; otherwise screen-reader only, because a
          `role="status"` with no text announces nothing at all. */}
      <span
        style={
          label
            ? { fontSize: 12.5, color: 'var(--rm-navy-70, #4a5768)', letterSpacing: '0.01em' }
            : { width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }
        }
      >
        {label || 'Loading'}
      </span>
    </div>
  );
}

/**
 * Nothing is on screen yet — app boot, auth, a route with no shell around it.
 *
 * Same mark, larger, on the product background. It replaces the periwinkle robot the fork
 * inherited from ServiceDesk, which was neither the palette nor the logo.
 */
export function FullPageLoader({ size = 52, label }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        background: 'var(--rm-surface, #fff)',
      }}
    >
      <PageLoader size={size} label={label} py={0} minHeight={0} />
    </div>
  );
}
